import React, { useState, useEffect, useRef } from 'react';
import { validateUrl, validateTitle, formatUrl } from '../utils/validation';
import { useLanguage } from '../contexts/LanguageContext';
import { useCategories } from '../contexts/CategoryContext';
import { stripCategoryPrefix } from '../utils/categoryKeywords';
import { FaEllipsisV, FaSave, FaTrash } from 'react-icons/fa';
import StarRating from './StarRating';
import '../styles/BookmarkForm.css';

const BookmarkForm = ({ bookmark, voiceData, onSubmit, onCancel, onDelete, categories: propCategories }) => {
  const { t } = useLanguage();
  const { categories: contextCategories, addCategory } = useCategories();
  const availableCategories = propCategories || contextCategories;
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: '',
    rating: 0
  });
  const [newCategory, setNewCategory] = useState('');
  const [categoryMode, setCategoryMode] = useState('select'); // 'select' or 'new'
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [showMenu]);

  // Populate form with bookmark data when editing OR voice data when adding
  useEffect(() => {
    if (bookmark) {
      // Editing existing bookmark - strip "categories." prefix from legacy data
      setFormData({
        title: bookmark.title || '',
        url: bookmark.url || '',
        description: bookmark.description || '',
        category: stripCategoryPrefix(bookmark.category) || '',
        rating: bookmark.rating || 0
      });
    } else if (voiceData) {
      // Adding new bookmark with voice data
      console.log('📝 Populating form with voice data:', voiceData);
      setFormData({
        title: voiceData.title || '',
        url: voiceData.url || '',
        description: voiceData.description || '',
        category: stripCategoryPrefix(voiceData.category) || '',
        rating: voiceData.rating || 0
      });
    } else {
      // New empty bookmark
      setFormData({
        title: '',
        url: '',
        description: '',
        category: '',
        rating: 0
      });
    }
  }, [bookmark, voiceData]);

  // Ensure a default category is always selected when available (only for NEW bookmarks)
  useEffect(() => {
    // Only set default category if:
    // 1. Not editing an existing bookmark (bookmark is null/undefined)
    // 2. No voice data with category
    // 3. No category is currently selected
    // 4. Categories are available
    if (!bookmark && !voiceData?.category && !showNewCategoryInput && (!formData.category || formData.category === '') && Array.isArray(availableCategories) && availableCategories.length > 0) {
      setFormData(prev => ({ ...prev, category: availableCategories[0] }));
    }
  }, [availableCategories, showNewCategoryInput, bookmark, voiceData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    const titleError = validateTitle(formData.title);
    if (titleError) newErrors.title = titleError;

    const urlError = validateUrl(formData.url);
    if (urlError) newErrors.url = urlError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        ...formData,
        url: formatUrl(formData.url),
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: showNewCategoryInput && newCategory.trim() ? newCategory.trim() : formData.category
      };

      console.log('📝 BookmarkForm submitting:', {
        bookmarkId: bookmark?.id,
        submissionData,
        categoryMode,
        newCategory,
        originalFormData: formData
      });

      // Add new category to available categories if it's new
      if (showNewCategoryInput && newCategory.trim() && !availableCategories.includes(newCategory.trim())) {
        addCategory(newCategory.trim());
        console.log('➕ Added new category:', newCategory.trim());
      }

      if (bookmark) {
        console.log('✏️ Updating existing bookmark...');
        await onSubmit(bookmark.id, submissionData);
      } else {
        console.log('➕ Creating new bookmark...');
        await onSubmit(submissionData);
      }

      console.log('✅ BookmarkForm submission completed');

      // Reset form
      setFormData({ title: '', url: '', description: '', category: '' });
      setNewCategory('');
      setCategoryMode('select');
      setErrors({});
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      setErrors({ submit: 'An error occurred while saving the bookmark' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dynamic-panel">
      <div className="panel-header">
        <h2>{bookmark ? t('editBookmark') : t('addBookmark')}</h2>
        <button
          type="button"
          className="close-btn"
          onClick={onCancel}
          title="Schließen"
        >
          ✕
        </button>
      </div>

      <div className="panel-content">
        <form className="bookmark-form" onSubmit={handleSubmit}>
          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="form-group">
          <div className="form-label-row">
            <label htmlFor="title">{t('title')} *</label>
            <div className="form-overflow-container" ref={menuRef}>
              <button
                type="button"
                className="form-overflow-btn"
                onClick={() => setShowMenu(!showMenu)}
                aria-label="Menü"
              >
                <FaEllipsisV />
              </button>
              {showMenu && (
                <div className="form-overflow-menu">
                  <button
                    type="submit"
                    className="overflow-menu-item"
                    disabled={isSubmitting}
                  >
                    <FaSave />
                    <span>{isSubmitting ? t('saving') : (bookmark ? t('updateBookmark') : t('addBookmarkBtn'))}</span>
                  </button>
                  {bookmark && (
                    <button
                      type="button"
                      className="overflow-menu-item overflow-menu-item-danger"
                      onClick={() => {
                        if (window.confirm(t('deleteConfirm') || 'Wirklich löschen?')) {
                          onDelete(bookmark.id);
                          onCancel();
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      <FaTrash />
                      <span>{t('delete') || 'Löschen'}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder={t('titlePlaceholder')}
            className={errors.title ? 'error' : ''}
            disabled={isSubmitting}
          />
          {errors.title && <span className="field-error">{errors.title}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="url">{t('url')} *</label>
          <input
            type="text"
            id="url"
            name="url"
            value={formData.url}
            onChange={handleInputChange}
            placeholder={t('urlPlaceholder')}
            className={errors.url ? 'error' : ''}
            disabled={isSubmitting}
          />
          {errors.url && <span className="field-error">{errors.url}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="description">{t('description')}</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t('descriptionPlaceholder')}
            rows="3"
            disabled={isSubmitting}
          />
        </div>

        {/* Star Rating Section */}
        <div className="form-group">
          <label htmlFor="rating">{t('rating') || 'Bewertung'}</label>
          <StarRating
            value={formData.rating}
            onChange={(newRating) => {
              setFormData(prev => ({
                ...prev,
                rating: newRating
              }));
            }}
            readOnly={false}
            maxStars={4}
            size="large"
            showEmpty={true}
          />
          <span className="rating-helper-text">
            {formData.rating === 0
              ? (t('noRating') || 'Keine Bewertung')
              : `${formData.rating} ${formData.rating === 1 ? 'Stern' : 'Sterne'}`
            }
          </span>
        </div>

        {/* Category Section */}
        <div className="form-group category-section">
          <label>{t('category') || 'Kategorie'}</label>

          {/* Always show category select with a default value */}
          <div className="category-row">
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className="category-select"
            >
              {Array.isArray(availableCategories) && availableCategories.length > 0 ? null : (
                <option value="">-- Keine Kategorie --</option>
              )}
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{stripCategoryPrefix(cat)}</option>
              ))}
            </select>
          </div>
        </div>

        </form>
      </div>
    </div>
  );
};

export default BookmarkForm;